// src/pages/billing/BillingSettingsPage.js — in-house billing management:
// plan status, payment method, invoice history, cancel/resume with survey.
// Replaces the Stripe customer-portal redirect.
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import CancelSurveyModal from '../../components/billing/CancelSurveyModal';
import CardUpdateForm from '../../components/billing/CardUpdateForm';
import RedeemGiftCardForm from '../../components/billing/RedeemGiftCardForm';

const API = process.env.REACT_APP_API_URL;

const formatMoney = (amount, currency) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount / 100);

const formatDate = (value) => {
    if (!value) return null;
    const str = String(value);
    const d = new Date(str.includes('T') ? str : str.split(' ')[0] + 'T00:00:00Z');
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

const cardClass = 'rounded-2xl border border-white/10 bg-[#0b0e14]/60 backdrop-blur-sm p-6 sm:p-7';

const SectionHeading = ({ children }) => (
    <div className="flex items-center gap-2 mb-4">
        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400" />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-neutral-400">{children}</h2>
    </div>
);

const BillingSettingsPage = () => {
    const { refreshEntitlements } = useAuth();
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [subLoading, setSubLoading] = useState(false);
    const [editingCard, setEditingCard] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const fetchOverview = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const res = await fetch(`${API}/api/get_billing_overview.php`, {
                credentials: 'include', headers: { 'Accept': 'application/json' },
            });
            const data = await res.json();
            if (res.ok && data.status === 'success') setOverview(data);
            else setLoadError(true);
        } catch (e) {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOverview(); }, [fetchOverview]);

    const handleSetCancel = async (cancel, reason, comment) => {
        setSubLoading(true);
        setMessage({ type: '', text: '' });
        try {
            const res = await fetch(`${API}/api/set_subscription_cancel.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify(cancel ? { cancel: true, reason, ...(comment ? { comment } : {}) } : { cancel: false }),
            });
            const result = await res.json();
            if (res.ok && result.status === 'success') {
                if (refreshEntitlements) await refreshEntitlements();
                setMessage({ type: 'success', text: cancel ? 'Your subscription will end at the period’s close.' : 'Your subscription has been resumed.' });
                fetchOverview();
            } else {
                setMessage({ type: 'error', text: result.message || 'Could not update your subscription.' });
            }
        } catch (e) {
            setMessage({ type: 'error', text: 'Something went wrong. Please try again.' });
        } finally {
            // Close the modal on failure too — the page-level error message is
            // invisible under the full-viewport overlay.
            setSubLoading(false);
            setShowCancelModal(false);
        }
    };

    const handleCardVerified = useCallback(async (setupIntentId) => {
        setMessage({ type: '', text: '' });
        try {
            const res = await fetch(`${API}/api/confirm_card_update.php`, {
                method: 'POST', credentials: 'include',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({ setup_intent_id: setupIntentId }),
            });
            const result = await res.json();
            if (res.ok && result.status === 'success' && result.card) {
                setOverview((prev) => (prev ? { ...prev, card: result.card } : prev));
                setEditingCard(false);
                setMessage({ type: 'success', text: 'Your card has been updated.' });
            } else {
                setMessage({ type: 'error', text: result.message || 'Could not update your card. Please try again.' });
            }
        } catch (e) {
            setMessage({ type: 'error', text: 'Could not update your card. Please try again.' });
        }
    }, []);

    // 3DS can bounce the user through a redirect; Stripe returns them here
    // with the setup intent in the query string.
    useEffect(() => {
        if (searchParams.get('setup') !== '1') return;
        const siId = searchParams.get('setup_intent');
        if (siId && searchParams.get('redirect_status') === 'succeeded') {
            // The confirm can win the race against the initial overview fetch
            // (overview still null → the card merge is a no-op), so refetch after.
            handleCardVerified(siId).then(() => fetchOverview());
        } else if (siId) {
            setMessage({ type: 'error', text: 'Card update was not completed. Please try again.' });
        }
        navigate('/settings/billing', { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-32 text-neutral-400">
                <div className="h-8 w-8 rounded-full border-2 border-neutral-700 border-t-cyan-400 animate-spin mb-4" />
                Loading billing…
            </div>
        );
    }

    if (loadError || !overview) {
        return (
            <div className="px-4 py-16 text-white text-center">
                <div className="max-w-md mx-auto bg-[#0b0e14]/60 border border-red-900/40 rounded-xl p-6">
                    <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
                    <p className="text-neutral-400 text-sm mb-4">We couldn't load your billing details.</p>
                    <button onClick={fetchOverview}
                        className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-6 rounded-lg">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    const sub = overview.subscription;
    const cancelScheduled = !!sub?.cancel_at_period_end;
    const isPremium = overview.plan === 'premium';
    const isAnnual = overview.premium_source === 'annual';
    const isStudent = overview.premium_source === 'student';
    const showCard = !!(overview.card || sub);
    const showInvoices = overview.invoices.length > 0;

    return (
        // Spacing/centering is owned by SettingsLayout's pane — same width as
        // the other settings pages so switching tabs doesn't shift content.
        <div className="text-white">
            <div className="max-w-2xl">
                <header className="mb-8">
                    <h1 className="text-3xl font-bold tracking-tight">Billing</h1>
                    <p className="text-sm text-neutral-400 mt-1">Manage your plan and payment details.</p>
                </header>

                {message.text && (
                    <p className={`mb-4 text-sm ${message.type === 'error' ? 'text-red-400' : 'text-emerald-400'}`}>{message.text}</p>
                )}

                {/* Plan */}
                <section className={`${cardClass} mb-6`}>
                    <SectionHeading>Plan</SectionHeading>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold ${isPremium ? 'bg-cyan-500/15 text-cyan-300 ring-1 ring-cyan-500/40' : 'bg-white/5 text-neutral-300 ring-1 ring-white/10'}`}>
                                {isPremium ? '★ Premium' : 'Free'}
                            </span>
                            <div className="mt-2 text-sm text-neutral-300">
                                {!isPremium && !sub && (
                                    <p className="text-neutral-400">5 AI optimizations/day · download locked</p>
                                )}
                                {isPremium && isAnnual && (
                                    <p>Annual pass — Premium until <span className="text-white font-medium">{formatDate(overview.premium_until)}</span>. Won't auto-renew.</p>
                                )}
                                {isPremium && isStudent && (
                                    <p>Student trial — Premium until <span className="text-white font-medium">{formatDate(overview.premium_until)}</span>.</p>
                                )}
                                {sub && sub.status === 'trialing' && !cancelScheduled && (
                                    <p>Free trial — first charge of {formatMoney(sub.amount, sub.currency)} on <span className="text-white font-medium">{formatDate(sub.current_period_end)}</span>.</p>
                                )}
                                {sub && sub.status !== 'trialing' && !cancelScheduled && (
                                    <p>Renews on <span className="text-white font-medium">{formatDate(sub.current_period_end)}</span> — {formatMoney(sub.amount, sub.currency)}{sub.interval === 'year' ? '/yr' : sub.interval === 'month' ? '/mo' : ''}.</p>
                                )}
                                {sub && cancelScheduled && (
                                    <p className="text-amber-300">Cancels — Premium until <span className="font-medium">{formatDate(sub.current_period_end)}</span>.</p>
                                )}
                            </div>
                        </div>
                        {!isPremium && !sub && (
                            <Link to="/upgrade" className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-5 rounded-lg transition-colors shadow-lg shadow-cyan-500/20">
                                Upgrade
                            </Link>
                        )}
                    </div>
                    {(overview.credit_balance_cents || 0) > 0 && (
                        <p className="mt-4 text-sm text-emerald-300">
                            Account credit: {formatMoney(overview.credit_balance_cents, 'usd')}
                            <span className="text-neutral-400"> — applies to your future invoices automatically.</span>
                        </p>
                    )}
                    {sub && (
                        <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap gap-2">
                            {!cancelScheduled && (
                                <button onClick={() => setShowCancelModal(true)} disabled={subLoading}
                                    className="text-sm font-medium text-neutral-400 hover:text-red-300 py-2 px-3 rounded-lg disabled:opacity-50 transition-colors">
                                    Cancel subscription
                                </button>
                            )}
                            {cancelScheduled && (
                                <button onClick={() => handleSetCancel(false)} disabled={subLoading}
                                    className="text-sm font-semibold text-cyan-300 hover:text-cyan-200 bg-cyan-500/10 border border-cyan-500/30 py-2 px-4 rounded-lg disabled:opacity-50 transition-colors">
                                    {subLoading ? 'Working…' : 'Resume subscription'}
                                </button>
                            )}
                        </div>
                    )}
                </section>

                {/* Payment method */}
                {showCard && (
                    <section className={`${cardClass} mb-6`}>
                        <SectionHeading>Payment method</SectionHeading>
                        {editingCard ? (
                            <CardUpdateForm onVerified={handleCardVerified} onCancel={() => setEditingCard(false)} />
                        ) : (
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                {overview.card ? (
                                    <p className="text-sm text-neutral-200">
                                        <span className="capitalize font-medium">{overview.card.brand}</span> •••• {overview.card.last4}
                                        <span className="text-neutral-400"> — expires {overview.card.exp_month}/{overview.card.exp_year}</span>
                                    </p>
                                ) : (
                                    <p className="text-sm text-neutral-400">No card on file.</p>
                                )}
                                <button onClick={() => setEditingCard(true)}
                                    className="text-sm font-medium text-neutral-200 bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg transition-colors">
                                    {overview.card ? 'Update card' : 'Add card'}
                                </button>
                            </div>
                        )}
                    </section>
                )}

                {/* Gift card */}
                <section className={`${cardClass} mb-6`}>
                    <SectionHeading>Gift card</SectionHeading>
                    <RedeemGiftCardForm onRedeemed={fetchOverview} />
                </section>

                {/* Invoices */}
                {showInvoices && (
                    <section className={cardClass}>
                        <SectionHeading>Invoices</SectionHeading>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-neutral-500 border-b border-white/10">
                                    <th className="py-2 font-medium">Date</th>
                                    <th className="py-2 font-medium">Amount</th>
                                    <th className="py-2 font-medium">Status</th>
                                    <th className="py-2 font-medium text-right">Receipt</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overview.invoices.map((inv) => (
                                    <tr key={inv.id} className="border-b border-white/5 last:border-0">
                                        <td className="py-2.5 text-neutral-300">{formatDate(inv.created)}</td>
                                        <td className="py-2.5">{formatMoney(inv.amount, inv.currency)}</td>
                                        <td className="py-2.5">
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${inv.status === 'paid' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-amber-500/15 text-amber-300'}`}>
                                                {inv.status}
                                            </span>
                                        </td>
                                        <td className="py-2.5 text-right">
                                            {inv.invoice_pdf && (
                                                <a href={inv.invoice_pdf} target="_blank" rel="noopener noreferrer"
                                                    className="text-cyan-300 hover:text-cyan-200">
                                                    PDF
                                                </a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                )}
            </div>

            {showCancelModal && sub && (
                <CancelSurveyModal
                    premiumUntilLabel={formatDate(sub.current_period_end)}
                    busy={subLoading}
                    onConfirm={(reason, comment) => handleSetCancel(true, reason, comment)}
                    onClose={() => setShowCancelModal(false)}
                />
            )}
        </div>
    );
};

export default BillingSettingsPage;
